<template>
    <section v-if="accessDenied" class="qr-location qr-location--denied" data-test="qr-location-denied">
        <div class="qr-location__content">
            <Icon name="qr-location-access-denied" class="qr-location__art" />
            <h1>{{ $t('qr.location.access_required_title') }}</h1>
            <p>{{ $t('qr.location.access_required_description') }}</p>
        </div>
        <PermissionHelp capability="location" compact class="qr-location__help" />
        <div class="qr-location__actions">
            <button
                class="btn btn-primary qr-location__share"
                type="button"
                data-test="qr-location-retry"
                :disabled="verificationStatus === 'pending'"
                @click="shareLocation"
            >
                {{ $t('qr.call.try_again') }}
            </button>
            <button
                class="btn btn-secondary qr-location__share"
                type="button"
                data-test="qr-location-home"
                @click="goToHome"
            >
                {{ $t('qr.location.go_to_home') }}
            </button>
        </div>
    </section>

    <section v-else class="qr-location" data-test="qr-location">
        <router-link
            :to="{ name: QR_ROUTES.HOME, params: { entranceId } }"
            class="qr-location__back"
            :aria-label="$t('qr.search.back')"
            data-test="qr-location-back"
        >
            <Icon name="arrow-left" class="w-6 h-6" />
        </router-link>
        <div class="qr-location__content">
            <Icon name="qr-map-location" class="qr-location__art" />
            <h1>{{ $t('qr.location.title') }}</h1>
            <p>{{ $t('qr.location.description') }}</p>
            <p v-if="errorMessage" class="qr-location__error" role="alert" data-test="qr-location-error">
                {{ $t(errorMessage) }}
            </p>
            <p v-else-if="verificationStatus === 'pending'" class="qr-location__status" role="status" aria-live="polite">
                {{ $t('qr.location.checking') }}
            </p>
        </div>
        <button
            class="btn btn-primary qr-location__share"
            type="button"
            data-test="qr-location-share"
            :disabled="verificationStatus === 'pending'"
            @click="shareLocation"
        >
            {{ $t(verificationStatus === 'pending' ? 'qr.location.checking_button' : 'qr.location.share') }}
        </button>
    </section>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Icon from '@/components/ui/Icon';
import PermissionHelp from '../../components/qr/PermissionHelp';
import { useQrVisitorContext } from '../../composables/qr/useQrVisitorContext';
import { QR_ROUTES } from '../../constants/qr';

const route = useRoute();
const router = useRouter();
const visitor = useQrVisitorContext();
const { locationStatus, locationPermissionState, verificationStatus } = visitor;
const entranceId = computed(() => route.params.entranceId);
const accessDenied = computed(() => locationStatus.value === 'denied' ||
    (locationStatus.value === 'idle' && locationPermissionState.value === 'denied'));
const errorMessage = computed(() => {
    if (verificationStatus.value === 'error') return 'qr.location.verify_error';
    if (['denied', 'unavailable', 'timeout', 'unsupported'].includes(locationStatus.value)) {
        return `qr.location.${locationStatus.value}`;
    }
    return null;
});

async function shareLocation() {
    const currentEntranceId = entranceId.value;
    const verified = await visitor.verifyLocation(currentEntranceId);
    if (verified && route.name === QR_ROUTES.LOCATION && entranceId.value === currentEntranceId) {
        await router.replace({ name: QR_ROUTES.SEARCH, params: { entranceId: currentEntranceId } });
    }
}

function goToHome() {
    router.replace({ name: QR_ROUTES.HOME, params: { entranceId: entranceId.value } });
}

onMounted(() => {
    if (visitor.isVerifiedFor(entranceId.value)) {
        router.replace({ name: QR_ROUTES.SEARCH, params: { entranceId: entranceId.value } });
    } else {
        visitor.refreshLocationPermission();
    }
});
</script>

<style scoped>
.qr-location {
    display: flex;
    min-height: calc(100vh - 3rem);
    min-height: calc(100dvh - 3rem);
    width: 100%;
    max-width: 480px;
    flex-direction: column;
    align-items: center;
    margin: 0 auto;
    padding: 12px 16px calc(16px + env(safe-area-inset-bottom));
    font-family: sofia-pro, Helvetica, Arial, sans-serif;
    color: #222;
}

.qr-location--denied {
    flex: 1;
    min-height: 0;
    padding: 24px 16px calc(18px + env(safe-area-inset-bottom));
    background: #fff;
}

.qr-location__help {
    flex-shrink: 0;
    margin: 0 0 16px;
}

.qr-location__back {
    align-self: flex-start;
    display: flex;
    color: #222;
}

.qr-location__content {
    display: flex;
    flex: 1;
    width: 100%;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding: clamp(40px, 12vh, 100px) 12px 24px;
    text-align: center;
}

.qr-location--denied .qr-location__content {
    padding: clamp(64px, 11.7vh, 95px) 12px 24px;
}

.qr-location__actions {
    display: flex;
    width: 100%;
    flex-direction: column;
    gap: 12px;
    margin-top: auto;
}

/* Cancel the global `.btn + .btn` left margin so stacked buttons stay aligned */
.qr-location__actions .btn + .btn {
    margin-left: 0;
}

.qr-location__art {
    width: 280px;
    height: auto;
    margin-bottom: 42px;
}

.qr-location--denied .qr-location__art {
    margin-bottom: 44px;
}

.qr-location__art :deep(svg) {
    width: 100%;
    height: auto;
}

.qr-location h1 {
    margin-bottom: 12px;
    font-size: 17px;
    font-weight: 700;
    line-height: 22px;
    text-transform: uppercase;
}

.qr-location--denied h1 {
    margin-bottom: 12px;
}

.qr-location p {
    margin: 0;
    font-family: sofia-pro, Helvetica, Arial, sans-serif;
    color: #696969;
    font-size: 14px;
    font-weight: 300;
    line-height: 22px;
    overflow-wrap: anywhere;
}

.qr-location__error {
    margin-top: 16px !important;
    color: #b42318 !important;
}

.qr-location__status {
    margin-top: 16px !important;
}

.qr-location__share {
    width: 100%;
    min-height: 40px;
    flex-shrink: 0;
}

.qr-location__share:disabled {
    opacity: 0.7;
}
</style>
